const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-6 md:p-8 shadow-lg border border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center border-b border-gray-200 pb-4">
            Privacy Policy
          </h1>

          <div className="prose max-w-none">
            <p className="text-gray-700 mb-6 text-lg leading-relaxed">
              At <strong className="text-blue-600">Busiconn</strong> (operated by Mansud Enterprises Pvt. Ltd.) your privacy matters. This Privacy Policy explains what personal information we collect, how we use it, and your rights. Busiconn enables users to connect for employment and business deals, including monetary transactions.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Information We Collect
              </h2>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-4">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1.5 font-bold">•</span>
                    <span>Personal information such as your name, email address, phone number, and KYC documentation where applicable.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1.5 font-bold">•</span>
                    <span>Business-related data like product/service listings and post content.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1.5 font-bold">•</span>
                    <span>Payment and transaction data related to deals conducted via the platform.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1.5 font-bold">•</span>
                    <span>Usage data including pages visited, chat interactions, and deal stages.</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 border-l-4 border-l-yellow-500">
                <div className="flex items-start gap-3">
                  <span className="text-yellow-600 text-xl">⚠️</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Monitoring Notice</h4>
                    <p className="text-gray-700">
                      Busiconn reserves the right to monitor user conversations to prevent fraud, enforce escrow terms, and ensure compliance with platform policies. No personal information will be disclosed or misused.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                How We Use Your Information
              </h2>
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1.5 font-bold">•</span>
                    <span>To provide, operate, and maintain our services including job discovery and business deal facilitation.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1.5 font-bold">•</span>
                    <span>To ensure security and compliance, including payment and identity verification.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1.5 font-bold">•</span>
                    <span>To enhance user experience, develop new features, and communicate important updates.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1.5 font-bold">•</span>
                    <span>To prevent fraud, unauthorized activity, and to comply with legal obligations.</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Data Sharing
              </h2>
              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 border-l-4 border-l-purple-500">
                  <div className="flex items-start gap-3">
                    <span className="text-purple-600 text-xl">🔒</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Our Commitment</h4>
                      <p className="text-gray-700">
                        We do not sell your personal information.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-gray-900 mb-4">We may share your data with:</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-orange-600 mt-1.5 font-bold">•</span>
                      <span>Third-party services like payment processors (e.g., Cashfree) to complete transactions.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-orange-600 mt-1.5 font-bold">•</span>
                      <span>Law enforcement or regulatory bodies if required by applicable law.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Your Rights
              </h2>
              <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-indigo-600 text-2xl">👤</span>
                  <h4 className="font-semibold text-gray-900">You have the right to:</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="text-indigo-600 mt-1.5 font-bold">•</span>
                    <span>Access or update your personal information from your account settings.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-indigo-600 mt-1.5 font-bold">•</span>
                    <span>Request deletion of your account or certain personal data.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-indigo-600 mt-1.5 font-bold">•</span>
                    <span>Opt out of promotional emails and marketing messages.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-indigo-600 mt-1.5 font-bold">•</span>
                    <span>Raise a complaint with us if you believe your rights are violated.</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Data Security
              </h2>
              <div className="bg-orange-400 p-4 rounded-lg border border-rose-200 border-l-4 border-l-rose-500">
                <div className="flex items-start gap-3">
                  <span className="text-rose text-xl">🛡️</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Security Measures</h4>
                    <p className="text-gray-700">
                      We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Contact Us
              </h2>
              <p className="text-gray-700 mb-4">
                For privacy-related inquiries:
              </p>
              <div className="bg-gray-100 rounded-lg p-6 border border-gray-200">
                <div className="text-gray-800 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📧</span>
                    <span>Email: <span className="text-blue-600 font-medium">contact@busiconn.com</span></span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📞</span>
                    <span>Phone: <span className="text-blue-600 font-medium">+91-9110067500</span></span>
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-sm mt-6 text-center italic border-t border-gray-200 pt-4">
                Last Updated: {new Date().toLocaleDateString("en-IN")}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;